import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const pillars = [
  {
    title: "Data Protection",
    text: "Protecting customer and operational data throughout its lifecycle.",
  },
  {
    title: "Secure Operations",
    text: "Structured processes for managing information securely across our organisation.",
  },
  {
    title: "Continuous Improvement",
    text: "Regular review and improvement of our information security practices.",
  },
];

const Security = () => {
  const { i18n } = useTranslation();

  const title = "Security & Compliance | ISO/IEC 27001 Certified | Cetasol";
  const description =
    "Cetasol is ISO/IEC 27001 certified. Learn how we manage information security across our operations, data and customer partnerships.";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={i18n.language} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href="https://cetasol.com/security" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cetasol.com/security" />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section className="relative pt-32 pb-12">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">
                Security &amp; Compliance
              </p>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Built with security in mind
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Cetasol works with operational vessel data across fleets and markets worldwide.
                Information security is therefore an important part of how we develop our
                technology, manage data and work with our customers and partners.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ISO 27001 */}
        <section className="py-16 section-fade">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl grid md:grid-cols-[1fr,auto] gap-12 items-center">
              <div>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-5">
                  ISO/IEC 27001 Certified
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-8 max-w-2xl">
                  Cetasol is ISO/IEC 27001 certified, reflecting our structured approach to
                  information security management and the protection of information across our
                  operations.
                </p>
              </div>
              {/* Space for the ISO 27001 certification badge / certificate visual */}
              <div className="w-full md:w-56 aspect-square border border-border flex flex-col items-center justify-center gap-3 text-center px-4">
                <ShieldCheck className="h-10 w-10 text-primary" aria-hidden="true" />
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  ISO/IEC 27001
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Information security */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-10 max-w-3xl">
              Information security
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl">
              {pillars.map((pillar, i) => (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="border border-border p-8"
                >
                  <h3 className="font-display text-xl font-bold text-foreground mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{pillar.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Security;
